/**
 * generateDeviceFingerprint
 *
 * Produces a stable, anonymous hash string that identifies this browser/device.
 * It combines several browser properties that rarely change — NOT cookies or
 * localStorage, so clearing storage won't reset it.
 *
 * Used to gate "10 free welcome credits" — one claim per physical device.
 */
export async function generateDeviceFingerprint() {
  try {
    const parts = [];

    // 1. Screen geometry
    parts.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);

    // 2. Timezone
    parts.push(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

    // 3. Language
    parts.push(navigator.language || '');

    // 4. Hardware concurrency (CPU cores)
    parts.push(`cpu${navigator.hardwareConcurrency || 0}`);

    // 5. Platform
    parts.push(navigator.platform || '');

    // 6. Canvas fingerprint — renders text then reads pixel data
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 40;
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('aifield-fp', 2, 15);
      ctx.fillStyle = 'rgba(102,204,0,0.7)';
      ctx.fillText('aifield-fp', 4, 17);
      parts.push(canvas.toDataURL().slice(-64));
    } catch {
      parts.push('no-canvas');
    }

    // 7. WebGL renderer (GPU info)
    try {
      const gl = document.createElement('canvas').getContext('webgl');
      if (gl) {
        const dbg = gl.getExtension('WEBGL_debug_renderer_info');
        if (dbg) {
          parts.push(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL));
          parts.push(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL));
        }
      }
    } catch {
      parts.push('no-webgl');
    }

    // Combine and hash with SHA-256 via SubtleCrypto
    const raw = parts.join('|');
    const encoder = new TextEncoder();
    const data = encoder.encode(raw);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Absolute fallback — random but stored in localStorage so it persists at
    // least for the session even if nothing else works.
    const stored = localStorage.getItem('_af_dfp');
    if (stored) return stored;
    const fallback = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem('_af_dfp', fallback);
    return fallback;
  }
}
